import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';

import { ApiError } from '@starling/core/api/errors';
import { FormError } from '@/components/form/primitives';
import { categoryLabel, useCategoriesQuery, type ListingCategory } from '@/feed/hooks';
import { formatDate, formatPlace } from '@/lib/format';
import {
  useArchiveListingMutation,
  useMyListingsQuery,
  useUnarchiveListingMutation,
  type Listing,
  type ListingStatus,
} from './hooks';
import styles from './MyListingsPage.module.css';

// «Мои объявления» (V1.3-e2, закрывает пробел №10 накопителя) — список с
// `GET /vision/my/listings` (VD11): все организации вызывающего, все статусы
// включая removed. Карточка — вёрстка карточки ленты + строка «лента ·
// место · организация» (organization_name показываем ВСЕГДА: он уже в каждой
// строке ответа, а считать организации ради условия — лишний запрос) и
// статус-тег. Действия по статусу:
//   * active   — «Редактировать», «Снять с публикации» (инлайн-подтверждение:
//                Popconfirm-аналог, AntD в Vision нет);
//   * archived — «Редактировать» + «Опубликовать снова» (V1.3-e3, VD12:
//                unarchive с bump published_at — объявление всплывает в
//                начале ленты; то же инлайн-подтверждение; 403-гейт
//                организации → сообщение со ссылкой в «Мою организацию»);
//   * removed  — приглушённая карточка без действий, подпись «снято
//                модерацией» (PATCH/archive/unarchive по removed → 404, VD6).
// Фильтр-пилюли — `?status=` в query-string (паттерн ленты: состояние
// переживает переход в правку и обратно); «все» = без параметра.

const FILTERS = ['all', 'active', 'archived'] as const;
type Filter = (typeof FILTERS)[number];

/** Классы статус-тега — exhaustive по ListingStatus (VD8). */
const STATUS_CLASS: Record<ListingStatus, string> = {
  active: styles.statusActive ?? '',
  archived: styles.statusArchived ?? '',
  removed: styles.statusRemoved ?? '',
};

function MyListingCard({
  listing,
  categories,
}: {
  listing: Listing;
  categories: ListingCategory[] | undefined;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const archiveMutation = useArchiveListingMutation();
  const unarchiveMutation = useUnarchiveListingMutation();
  const [confirming, setConfirming] = useState<'archive' | 'unarchive' | null>(null);
  const [error, setError] = useState<'archive' | 'unarchive' | 'unarchiveGate' | null>(null);
  const [republished, setRepublished] = useState(false);
  const removed = listing.status === 'removed';
  const pending = archiveMutation.isPending || unarchiveMutation.isPending;

  const archive = (): void => {
    setError(null);
    setRepublished(false);
    archiveMutation.mutate(listing.id, {
      // Успех: инвалидация в мутации перезагрузит список — тег сменится сам.
      onError: () => {
        setConfirming(null);
        setError('archive');
      },
    });
  };

  const unarchive = (): void => {
    setError(null);
    unarchiveMutation.mutate(listing.id, {
      // Тег «Активно» появится сам после рефетча; заметка — подтверждение
      // ре-публикации (тостера в Vision нет — сообщения живут в карточке).
      onSuccess: () => {
        setConfirming(null);
        setRepublished(true);
      },
      onError: (err) => {
        setConfirming(null);
        // 403 — организация расподтверждена/выключена (гейт как у публикации,
        // VD12): объясняем и ведём проверять статус в «Мою организацию».
        setError(err instanceof ApiError && err.status === 403 ? 'unarchiveGate' : 'unarchive');
      },
    });
  };

  return (
    <article className={removed ? `${styles.card} ${styles.cardRemoved}` : styles.card}>
      <div className={styles.cardTop}>
        <h3 className={styles.cardTitle}>{listing.title}</h3>
        <span className={styles.tag}>
          {categoryLabel(categories, listing.category_id, i18n.language)}
        </span>
      </div>
      <div className={styles.cardMeta}>
        <MapPin size={11} aria-hidden="true" />
        {t(`tabs.${listing.feed}`)} · {formatPlace(listing.city, listing.district)} ·{' '}
        {listing.organization_name}
      </div>
      <div className={styles.cardFooter}>
        <span className={styles.cardDate}>{formatDate(listing.published_at, i18n.language)}</span>
        <span className={`${styles.status} ${STATUS_CLASS[listing.status]}`}>
          {t(`myListings.status.${listing.status}`)}
        </span>
      </div>
      {removed ? (
        <p className={styles.removedNote}>{t('myListings.removedNote')}</p>
      ) : (
        <>
          {republished && (
            <p className={styles.successNote} role="status">
              {t('myListings.actions.unarchiveDone')}
            </p>
          )}
          {error !== null && (
            <FormError>
              {error === 'archive' && t('myListings.actions.archiveError')}
              {error === 'unarchive' && t('myListings.actions.unarchiveError')}
              {error === 'unarchiveGate' && (
                <>
                  {t('myListings.actions.unarchiveGate')}{' '}
                  <Link to="/my/organization" className={styles.errorLink}>
                    {t('post.gate.action')}
                  </Link>
                </>
              )}
            </FormError>
          )}
          <div className={styles.actions}>
            <Link
              to={`/my/listings/${listing.id}/edit`}
              state={{ listing }}
              className={styles.actionLink}
            >
              {t('myListings.actions.edit')}
            </Link>
            {listing.status === 'active' &&
              (confirming === 'archive' ? (
                <span className={styles.confirm}>
                  {t('myListings.actions.archiveConfirm')}
                  <button
                    type="button"
                    className={styles.confirmYes}
                    disabled={pending}
                    onClick={archive}
                  >
                    {t('myListings.actions.archiveYes')}
                  </button>
                  <button
                    type="button"
                    className={styles.confirmNo}
                    disabled={pending}
                    onClick={() => setConfirming(null)}
                  >
                    {t('myListings.actions.archiveCancel')}
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  className={styles.actionButton}
                  onClick={() => setConfirming('archive')}
                >
                  {t('myListings.actions.archive')}
                </button>
              ))}
            {listing.status === 'archived' &&
              (confirming === 'unarchive' ? (
                <span className={styles.confirm}>
                  {t('myListings.actions.unarchiveConfirm')}
                  <button
                    type="button"
                    className={styles.confirmPublish}
                    disabled={pending}
                    onClick={unarchive}
                  >
                    {t('myListings.actions.unarchiveYes')}
                  </button>
                  <button
                    type="button"
                    className={styles.confirmNo}
                    disabled={pending}
                    onClick={() => setConfirming(null)}
                  >
                    {t('myListings.actions.unarchiveCancel')}
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  className={styles.actionButton}
                  onClick={() => setConfirming('unarchive')}
                >
                  {t('myListings.actions.unarchive')}
                </button>
              ))}
          </div>
        </>
      )}
    </article>
  );
}

export function MyListingsPage(): JSX.Element {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const raw = searchParams.get('status');
  const filter: Filter = raw === 'active' || raw === 'archived' ? raw : 'all';
  const status = filter === 'all' ? undefined : filter;

  const query = useMyListingsQuery(status);
  const categories = useCategoriesQuery().data?.items;
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];

  function selectFilter(next: Filter): void {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === 'all') params.delete('status');
        else params.set('status', next);
        return params;
      },
      { replace: true },
    );
  }

  return (
    <div className={styles.wrap}>
      <h1 className={styles.pageTitle}>{t('myListings.title')}</h1>

      <div className={styles.filters} role="group" aria-label={t('myListings.filter.label')}>
        {FILTERS.map((key) => (
          <button
            key={key}
            type="button"
            aria-pressed={key === filter}
            className={`${styles.filterPill} ${key === filter ? styles.filterPillActive : ''}`}
            onClick={() => selectFilter(key)}
          >
            {t(`myListings.filter.${key}`)}
          </button>
        ))}
      </div>

      {query.isPending && <div className={styles.state}>{t('myListings.loading')}</div>}

      {query.isError && (
        <div className={`${styles.state} ${styles.stateError}`}>
          {t('myListings.error')}
          <div>
            <button type="button" className={styles.retry} onClick={() => void query.refetch()}>
              {t('myListings.retry')}
            </button>
          </div>
        </div>
      )}

      {query.isSuccess && items.length === 0 && (
        <div className={styles.state}>
          <p className={styles.emptyText}>
            {filter === 'all' ? t('myListings.empty') : t('myListings.emptyFiltered')}
          </p>
          <Link to="/post" className={styles.primaryLink}>
            {t('myListings.post')}
          </Link>
        </div>
      )}

      {items.length > 0 && (
        <div className={styles.list}>
          {items.map((listing) => (
            <MyListingCard key={listing.id} listing={listing} categories={categories} />
          ))}
        </div>
      )}

      {query.hasNextPage && (
        <div className={styles.moreRow}>
          <button
            type="button"
            className={styles.secondaryButton}
            disabled={query.isFetchingNextPage}
            onClick={() => void query.fetchNextPage()}
          >
            {query.isFetchingNextPage ? t('myListings.loadingMore') : t('myListings.loadMore')}
          </button>
        </div>
      )}
    </div>
  );
}
